import { createContext, useContext, useEffect, useMemo, useState, ReactNode, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { createLoginRequest } from "./msalConfig";
import type { TenantType } from "./azureAdConfig";
import { usePostHog } from "@posthog/react";
import { exchangeAzureToken } from "@/api/user/user";
import { setRefreshToken } from "@/api/axios";
import { TENANT_STORAGE_KEY } from "@/auth/authStorageKeys";

export interface AuthContextType {
    token: string | null;
    setToken: (token: string | null) => void;
    logout: () => void;
    login: () => Promise<void>;
    isAuthenticated: boolean;
    isLoading: boolean;
    selectedTenant: TenantType | null;
    authMethod: "azure" | "token" | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
    const { instance, accounts, inProgress } = useMsal();
    const [token, setToken] = useState<string | null>(null);
    const posthog = usePostHog();
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTenant, setSelectedTenant] = useState<TenantType | null>(() => {
        const stored = localStorage.getItem(TENANT_STORAGE_KEY);
        return stored as TenantType | null;
    });

    useEffect(() => {
        const exchangeToken = async (azureToken: string | null | undefined) => {
            if (!azureToken || azureToken == null || azureToken == undefined) {
                throw new Error("Token can't be null");
            }

            const response = await exchangeAzureToken(azureToken);
            setRefreshToken(response.data.refreshToken);
            return response.data.token;
        };
        const getToken = async () => {
            if (inProgress === InteractionStatus.None && accounts.length > 0) {
                const tenant = selectedTenant || (localStorage.getItem(TENANT_STORAGE_KEY) as TenantType);
                const tokenRequest = tenant ? createLoginRequest(tenant) : { scopes: ["User.Read"] };
                if (!tenant) {
                    console.warn("Tenant was falsy");
                }

                try {
                    const response = await instance.acquireTokenSilent({
                        ...tokenRequest,
                        account: accounts[0],
                    });
                    const azureToken = response.accessToken;
                    const token = await exchangeToken(azureToken);
                    setToken(token);
                    posthog.identify(accounts[0].username, {
                        name: accounts[0].name ?? accounts[0].username,
                        email: accounts[0].username,
                    });
                    // eslint-disable-next-line no-console
                    // console.log("Token: ", token);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("Failed to acquire token silently:", error);
                    setToken(null);

                    if (isAxiosError(error) && error.response?.status === 409) {
                        // Account-conflict responses aren't an auth failure - redirecting to /login would just
                        // re-run MSAL sign-in and hit the same conflict again, looping forever.
                        toast.error("This Microsoft account is linked to a conflicting Hockeyly account. Please contact support.");
                    } else {
                        // Clear storage and redirect to login if token acquisition fails
                        localStorage.removeItem(TENANT_STORAGE_KEY);
                        // await instance.clearCache();
                        window.location.href = "/login";
                    }
                }
            } else if (inProgress === InteractionStatus.None && accounts.length === 0) {
                setToken(null);
            }

            if (inProgress === InteractionStatus.None) {
                setIsLoading(false);
            }
        };

        getToken();
    }, [instance, accounts, inProgress, selectedTenant, posthog]);

    const login = useCallback(async () => {
        await instance.loginRedirect({ scopes: ["User.Read"] });
    }, [instance]);

    const logout = useCallback(async () => {
        posthog.reset();
        setToken(null);
        setRefreshToken(null);
        localStorage.removeItem(TENANT_STORAGE_KEY);
        setSelectedTenant(null);

        const accounts = instance.getAllAccounts();
        for (const account of accounts) {
            await instance.clearCache({ account });
        }

        instance.logoutRedirect();
    }, [instance, posthog]);

    const contextValue = useMemo(
        () => ({
            token,
            setToken,
            logout,
            login,
            isAuthenticated: !!token && accounts.length > 0,
            isLoading,
            selectedTenant,
            authMethod: "azure" as const,
        }),
        [token, accounts.length, isLoading, login, logout, selectedTenant]
    );

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export default AuthProvider;
